import React, { useState } from 'react';
import { fmtDate } from '../utils/dateUtils';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, IconButton, Tooltip, Table, TableHead,
  TableRow, TableCell, Tabs, Tab, Accordion, AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  MY_TIMELINE_EVENTS_QUERY, MY_CEREMONIES_QUERY,
  CREATE_TIMELINE_EVENT_MUTATION, UPDATE_TIMELINE_EVENT_MUTATION, DELETE_TIMELINE_EVENT_MUTATION,
  CREATE_TIMELINE_TASK_MUTATION, UPDATE_TIMELINE_TASK_MUTATION, DELETE_TIMELINE_TASK_MUTATION,
} from '../graphql/queries';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const EVENT_TYPES  = ['task','milestone','ceremony','reminder','deadline'];
const STATUS_COLOR = { pending:'default', in_progress:'info', completed:'success', cancelled:'error' };
const PRIORITY_COLOR = { low:'default', medium:'warning', high:'error', critical:'error' };

const EMPTY_EVT = { title:'', ceremonyId:'', description:'', eventType:'task', startTime:'', endTime:'', allDay:false, assignedTo:'', color:'', sortOrder:0 };
const EMPTY_TSK = { title:'', description:'', assignedRole:'', assigneeName:'', dueDate:'', priority:'medium', notes:'' };

export default function TimelinePage() {
  const [tab, setTab]       = useState(0);
  const [error, setError]   = useState('');
  const [filterCeremony, setFilterCeremony] = useState('');

  // Event dialog
  const [evtDialog, setEvtDialog] = useState(false);
  const [editEvt, setEditEvt]     = useState(null);
  const [evtForm, setEvtForm]     = useState(EMPTY_EVT);

  // Task dialog
  const [tskDialog, setTskDialog]   = useState(false);
  const [editTsk, setEditTsk]       = useState(null);
  const [tskForm, setTskForm]       = useState(EMPTY_TSK);
  const [tskEventId, setTskEventId] = useState('');

  const { data: ceremonyData } = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: evtData, loading, refetch } = useQuery(MY_TIMELINE_EVENTS_QUERY, {
    variables: filterCeremony ? { ceremonyId: filterCeremony } : {},
    fetchPolicy: 'cache-and-network',
  });

  const ceremonies = ceremonyData?.myCeremonies?.filter(c => c.status !== 'cancelled') || [];
  const events     = evtData?.myTimelineEvents || [];
  const summary    = {
    total:     events.length,
    pending:   events.filter(e => e.status === 'pending').length,
    inProg:    events.filter(e => e.status === 'in_progress').length,
    done:      events.filter(e => e.status === 'completed').length,
  };

  const [createEvt] = useMutation(CREATE_TIMELINE_EVENT_MUTATION, { onCompleted: () => { setEvtDialog(false); setEvtForm(EMPTY_EVT); refetch(); setError(''); }, onError: e => setError(e.message) });
  const [updateEvt] = useMutation(UPDATE_TIMELINE_EVENT_MUTATION, { onCompleted: () => { setEvtDialog(false); setEditEvt(null); refetch(); setError(''); }, onError: e => setError(e.message) });
  const [deleteEvt] = useMutation(DELETE_TIMELINE_EVENT_MUTATION, { onCompleted: () => refetch() });

  const [createTsk] = useMutation(CREATE_TIMELINE_TASK_MUTATION, { onCompleted: () => { setTskDialog(false); setTskForm(EMPTY_TSK); refetch(); setError(''); }, onError: e => setError(e.message) });
  const [updateTsk] = useMutation(UPDATE_TIMELINE_TASK_MUTATION, { onCompleted: () => { setTskDialog(false); setEditTsk(null); refetch(); setError(''); }, onError: e => setError(e.message) });
  const [deleteTsk] = useMutation(DELETE_TIMELINE_TASK_MUTATION, { onCompleted: () => refetch() });

  const openCreateEvt = () => { setEditEvt(null); setEvtForm(EMPTY_EVT); setEvtDialog(true); };
  const openEditEvt   = (e) => { setEditEvt(e); setEvtForm({ title: e.title, ceremonyId: e.ceremonyId || '', description: e.description || '', eventType: e.eventType, startTime: e.startTime || '', endTime: e.endTime || '', allDay: !!e.allDay, assignedTo: e.assignedTo || '', color: e.color || '', sortOrder: e.sortOrder || 0 }); setEvtDialog(true); };
  const handleSaveEvt = () => {
    const vars = { ...evtForm, ceremonyId: evtForm.ceremonyId || null, sortOrder: Number(evtForm.sortOrder) || 0 };
    if (editEvt) updateEvt({ variables: { id: editEvt.id, ...vars } });
    else createEvt({ variables: vars });
  };

  const openCreateTsk = (eventId) => { setEditTsk(null); setTskForm(EMPTY_TSK); setTskEventId(eventId); setTskDialog(true); };
  const openEditTsk   = (t) => { setEditTsk(t); setTskForm({ title: t.title, description: t.description || '', assignedRole: t.assignedRole || '', assigneeName: t.assigneeName || '', dueDate: t.dueDate || '', priority: t.priority || 'medium', notes: t.notes || '' }); setTskDialog(true); };
  const handleSaveTsk = () => {
    if (editTsk) updateTsk({ variables: { id: editTsk.id, ...tskForm } });
    else createTsk({ variables: { eventId: tskEventId, ...tskForm } });
  };
  const quickStatusToggle = (task) => {
    const next = task.status === 'completed' ? 'pending' : 'completed';
    updateTsk({ variables: { id: task.id, status: next } });
  };

  const allTasks = events.flatMap(e => (e.tasks || []).map(t => ({ ...t, eventTitle: e.title })));

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Event Scheduling &amp; Timeline</Typography>
          <Typography variant="body2" color="text.secondary">Master schedule across all ceremonies — events, tasks, and dependencies.</Typography>
        </Box>
        {tab === 0 && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateEvt}>Add Event</Button>}
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Master Timeline" />
        <Tab label={`All Tasks (${allTasks.length})`} />
      </Tabs>

      {/* ── TAB 0: Master Timeline ─────────────────────────── */}
      {tab === 0 && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[{ label:'Total',       value: summary.total   },
              { label:'Pending',     value: summary.pending },
              { label:'In Progress', value: summary.inProg  },
              { label:'Completed',   value: summary.done    }].map(s => (
              <Grid item xs={6} sm={3} key={s.label}>
                <Card><CardContent sx={{ textAlign:'center', py:'12px !important' }}>
                  <Typography variant="h4" fontWeight={700}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent></Card>
              </Grid>
            ))}
          </Grid>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
            <Chip label="All Ceremonies" onClick={() => setFilterCeremony('')} color={!filterCeremony ? 'primary' : 'default'} variant={!filterCeremony ? 'filled' : 'outlined'} />
            {ceremonies.map(c => (
              <Chip key={c.id} label={c.name} onClick={() => setFilterCeremony(c.id === filterCeremony ? '' : c.id)} color={filterCeremony === c.id ? 'primary' : 'default'} variant={filterCeremony === c.id ? 'filled' : 'outlined'} />
            ))}
          </Stack>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          {events.length === 0 && !loading && <Alert severity="info">No timeline events yet. Click <strong>Add Event</strong> to build your master schedule.</Alert>}
          <Paged items={events}>{rows => rows.map(evt => (
            <Accordion key={evt.id} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: evt.color || 'primary.main', flexShrink: 0 }} />
                  <Typography fontWeight={600} sx={{ flexGrow: 1 }}>{evt.title}</Typography>
                  {evt.ceremony && <Chip label={evt.ceremony.name} size="small" variant="outlined" />}
                  <Chip label={evt.eventType} size="small" />
                  <Chip label={evt.status} size="small" color={STATUS_COLOR[evt.status] || 'default'} />
                  {evt.startTime && <Typography variant="caption" color="text.secondary">{fmtDate(evt.startTime)}</Typography>}
                  <Tooltip title="Edit event"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditEvt(evt); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete event"><IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteEvt({ variables: { id: evt.id } }); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {evt.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{evt.description}</Typography>}
                {evt.assignedTo   && <Typography variant="caption">Assigned to: <strong>{evt.assignedTo}</strong></Typography>}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="subtitle2">Tasks ({(evt.tasks || []).length})</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => openCreateTsk(evt.id)}>Add Task</Button>
                </Stack>
                {(evt.tasks || []).length === 0 && <Typography variant="caption" color="text.secondary">No tasks yet.</Typography>}
                {(evt.tasks || []).map(t => (
                  <Stack key={t.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <IconButton size="small" onClick={() => quickStatusToggle(t)} color={t.status === 'completed' ? 'success' : 'default'}>
                      {t.status === 'completed' ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                    </IconButton>
                    <Typography variant="body2" sx={{ flexGrow: 1, textDecoration: t.status === 'completed' ? 'line-through' : 'none' }}>{t.title}</Typography>
                    {t.priority !== 'medium' && <Chip label={t.priority} size="small" color={PRIORITY_COLOR[t.priority] || 'default'} />}
                    {t.dueDate  && <Typography variant="caption" color={new Date(t.dueDate) < new Date() && t.status !== 'completed' ? 'error' : 'text.secondary'}>{fmtDate(t.dueDate)}</Typography>}
                    {t.assignedRole && <Typography variant="caption" color="text.secondary">{t.assignedRole}{t.assigneeName ? ` — ${t.assigneeName}` : ''}</Typography>}
                    <Tooltip title="Edit task"><IconButton size="small" onClick={() => openEditTsk(t)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete task"><IconButton size="small" color="error" onClick={() => deleteTsk({ variables: { id: t.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </Stack>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}</Paged>
        </Box>
      )}

      {/* ── TAB 1: All Tasks ─────────────────────────────────── */}
      {tab === 1 && (
        <Box>
          {allTasks.length === 0 && <Alert severity="info">No tasks yet. Add tasks from within timeline events.</Alert>}
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Done','Task','Event','Role / Assignee','Due','Priority'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {allTasks.map(t => (
                <TableRow key={t.id} hover>
                  <TableCell>
                    <IconButton size="small" onClick={() => quickStatusToggle(t)} color={t.status === 'completed' ? 'success' : 'default'}>
                      {t.status === 'completed' ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ textDecoration: t.status === 'completed' ? 'line-through' : 'none' }}>{t.title}</TableCell>
                  <TableCell><Typography variant="caption">{t.eventTitle}</Typography></TableCell>
                  <TableCell>{t.assignedRole || '\u2014'}{t.assigneeName ? ` \u2014 ${t.assigneeName}` : ''}</TableCell>
                  <TableCell>
                    {t.dueDate
                      ? <Typography variant="caption" color={new Date(t.dueDate) < new Date() && t.status !== 'completed' ? 'error.main' : 'text.secondary'}>
                          {fmtDate(t.dueDate)}
                        </Typography>
                      : '\u2014'}
                  </TableCell>
                  <TableCell><Chip label={t.priority || 'medium'} size="small" color={PRIORITY_COLOR[t.priority] || 'default'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Event Dialog */}
      <Dialog open={evtDialog} onClose={() => setEvtDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editEvt ? 'Edit Event' : 'New Timeline Event'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" value={evtForm.title} onChange={e => setEvtForm(f => ({ ...f, title: e.target.value }))} fullWidth required />
            <Stack direction="row" spacing={2}>
              <TextField select label="Event Type" value={evtForm.eventType} onChange={e => setEvtForm(f => ({ ...f, eventType: e.target.value }))} fullWidth>
                {EVENT_TYPES.map(t => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
              </TextField>
              <TextField select label="Ceremony" value={evtForm.ceremonyId} onChange={e => setEvtForm(f => ({ ...f, ceremonyId: e.target.value }))} fullWidth>
                <MenuItem value="">None</MenuItem>
                {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Start Time" type="datetime-local" value={evtForm.startTime} onChange={e => setEvtForm(f => ({ ...f, startTime: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="End Time"   type="datetime-local" value={evtForm.endTime}   onChange={e => setEvtForm(f => ({ ...f, endTime: e.target.value }))}   fullWidth InputLabelProps={{ shrink: true }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Assigned To" value={evtForm.assignedTo} onChange={e => setEvtForm(f => ({ ...f, assignedTo: e.target.value }))} fullWidth />
              <TextField label="Color (hex)" value={evtForm.color} onChange={e => setEvtForm(f => ({ ...f, color: e.target.value }))} fullWidth placeholder="#4a90e2" />
              <TextField label="Sort Order" type="number" value={evtForm.sortOrder} onChange={e => setEvtForm(f => ({ ...f, sortOrder: e.target.value }))} fullWidth />
            </Stack>
            <TextField label="Description" value={evtForm.description} onChange={e => setEvtForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEvtDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEvt}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Task Dialog */}
      <Dialog open={tskDialog} onClose={() => setTskDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editTsk ? 'Edit Task' : 'New Task'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Task Title" value={tskForm.title} onChange={e => setTskForm(f => ({ ...f, title: e.target.value }))} fullWidth required />
            <Stack direction="row" spacing={2}>
              <TextField label="Assigned Role" value={tskForm.assignedRole} onChange={e => setTskForm(f => ({ ...f, assignedRole: e.target.value }))} fullWidth placeholder="e.g. Priest, Caterer" />
              <TextField label="Assignee Name" value={tskForm.assigneeName} onChange={e => setTskForm(f => ({ ...f, assigneeName: e.target.value }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Due Date" type="datetime-local" value={tskForm.dueDate} onChange={e => setTskForm(f => ({ ...f, dueDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
              <TextField select label="Priority" value={tskForm.priority} onChange={e => setTskForm(f => ({ ...f, priority: e.target.value }))} fullWidth>
                {['low','medium','high','critical'].map(p => <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField label="Description" value={tskForm.description} onChange={e => setTskForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />
            <TextField label="Notes" value={tskForm.notes} onChange={e => setTskForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTskDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTsk}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
